"use package";

import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "@/hooks";
import {
  fetchPackages,
  createPackage,
  updatePackage,
  deletePackage,
  activatePackage,
  deactivatePackage,
} from "@/store/modules/package/thunks";
import { AppDispatch } from "@/store";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { packageSchema } from "@/validations";
import { fetchServices } from "@/store/modules/services/thunks";
import { usePermission } from "@/hooks/usePermission/usePermission";
import { useAuth } from "@/context/AuthContext";
import { useApiReady } from "@/hooks/useApiReady";

const usePackage = (onSuccess?: () => void) => {
  const { params, setParams, handlePage } = useParams();
  const { per_page, current_page } = params;
  const { hasPermission, loading: loadingPermission } = usePermission();
  const { currency } = useAuth();
  const { isReady: isApiReady } = useApiReady();

  const [searchValue, setSearchValue] = useState<string>(params.search || "");

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(packageSchema),
    defaultValues: {
      name: "",
      validity_months: undefined,
      sale_commission_percentage: undefined,
      services: [
        {
          service_id: "",
          usage_limit: undefined,
          reference_price: undefined,
        },
      ],
    },
  });

  const setFormData = (data: Partial<typeof packageSchema.__outputType>) => {
    reset(data);
  };

  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);

  const onSubmit = async (data: typeof packageSchema.__outputType) => {
    try {
      let result;
      let payload: any = data;

      if (pendingImageFile) {
        const fd = new FormData();
        Object.entries(data).forEach(([k, v]) => {
          if (k === "services" && Array.isArray(v)) {
            v.forEach((srv: any, idx: number) => {
              if (srv.service_id)
                fd.append(
                  `services[${idx}][service_id]`,
                  String(srv.service_id),
                );
              if (srv.usage_limit !== undefined && srv.usage_limit !== null)
                fd.append(
                  `services[${idx}][usage_limit]`,
                  String(srv.usage_limit),
                );
              if (
                srv.reference_price !== undefined &&
                srv.reference_price !== null
              )
                fd.append(
                  `services[${idx}][reference_price]`,
                  String(srv.reference_price),
                );
            });
          } else if (v !== undefined && v !== null) {
            fd.append(k, String(v));
          }
        });
        fd.append("image", pendingImageFile);
        payload = fd;
      }

      if (packageId && hasPermission("update package")) {
        if (payload instanceof FormData) {
          (payload as FormData).append("_method", "PUT");
          result = await dispatch(
            updatePackage({ id: packageId, data: payload } as any),
          );
        } else {
          result = await dispatch(updatePackage({ id: packageId, ...payload }));
        }
      } else {
        if (hasPermission("create package")) {
          result = await dispatch(createPackage(payload));
        }
      }

      if (
        createPackage.fulfilled.match(result) ||
        updatePackage.fulfilled.match(result)
      ) {
        if (onSuccess) onSuccess();
        reset();
        setPackageId(null);
        setPendingImageFile(null);
        dispatch(fetchPackages(params));
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
    }
  };

  const { packages, loading, error } = useSelector(
    (state: any) => state.packages,
  );
  const dispatch: AppDispatch = useDispatch();

  const services = useSelector((state: any) => state.services.services);

  const [packageId, setPackageId] = useState<any>();

  const [currentPage, setCurrentPage] = useState(packages?.current_page ?? 1);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const totalItems = packages?.total ?? 0;
  const itemsPerPage = packages?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  // Função para gerar paginação inteligente
  const generateSmartPagination = (current: number, total: number) => {
    if (total <= 6) {
      // Se tem 6 páginas ou menos, mostra todas
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    // Sempre mostra a primeira página
    pages.push(1);

    if (current <= 4) {
      // Se estamos nas primeiras páginas (1, 2, 3, 4)
      pages.push(2, 3, 4);
      if (current === 4 && total > 5) {
        pages.push(5);
      }
      if (total > 5) {
        pages.push("...");
        pages.push(total);
      }
    } else if (current >= total - 3) {
      // Se estamos nas últimas páginas
      pages.push("...");
      for (let i = total - 3; i <= total; i++) {
        if (i > 1) pages.push(i);
      }
    } else {
      // Se estamos no meio
      pages.push("...");
      pages.push(current - 1, current, current + 1);
      pages.push("...");
      pages.push(total);
    }

    // Remove duplicatas mantendo a ordem
    const uniquePages: (number | string)[] = [];
    pages.forEach((page) => {
      if (!uniquePages.includes(page)) {
        uniquePages.push(page);
      }
    });

    return uniquePages;
  };

  const pagesArray = generateSmartPagination(currentPage, totalPages);

  useEffect(() => {
    if (packages?.current_page && packages.current_page !== currentPage) {
      setCurrentPage(packages.current_page);
    }
  }, [packages?.current_page]);

  // Efeito para monitorar mudanças no searchValue e atualizar params
  useEffect(() => {
    const updateParams = () => {
      if (searchValue !== params.search) {
        setParams({
          search: searchValue,
          current_page: 1, // Reset para a primeira página ao filtrar
        });
      }
    };

    // Debounce para evitar muitas chamadas à API
    const timeoutId = setTimeout(updateParams, 500);
    return () => clearTimeout(timeoutId);
  }, [searchValue, setParams, params.search]);

  const handleFilterChange = (value: string) => {
    setSearchValue(value);
  };

  useEffect(() => {
    if (!isApiReady) return;

    if (hasPermission("read package")) {
      dispatch(fetchPackages(params));
      // Request more services per page so the packages screen can show more options
      dispatch(fetchServices({ ...params, per_page: 200 }));
    }
  }, [params, isApiReady, dispatch]);

  const handleDelete = async () => {
    try {
      if (packageId && hasPermission("delete package")) {
        await dispatch(deletePackage(packageId));
        setPackageId(null);
        // Recarregar a lista de pacotes após deletar
        dispatch(fetchPackages(params));
        return true;
      }
    } catch (error) {
      console.error("Error deleting package:", error);
    }
  };

  // Optimistic UI for toggling package status (active/inactive)
  const [optimisticPackages, setOptimisticPackages] = useState<any[]>(
    packages?.data || [],
  );

  // Sync optimistic state when packages from redux are updated
  useEffect(() => {
    if (packages?.data) setOptimisticPackages(packages.data);
  }, [packages?.data]);

  const handleToggleActive = async (id: number | string) => {
    // Determine current status from optimistic state (falling back to redux packages)
    const pkgCurrent =
      optimisticPackages.find((p: any) => String(p.id) === String(id)) ||
      packages?.data?.find((p: any) => String(p.id) === String(id));

    const currentlyActive = pkgCurrent?.status === "active";

    // Apply optimistic toggle based on current status
    setOptimisticPackages((prev: any[]) =>
      prev.map((pkg) =>
        String(pkg.id) === String(id)
          ? { ...pkg, status: currentlyActive ? "inactive" : "active" }
          : pkg,
      ),
    );

    try {
      const result = await dispatch(
        currentlyActive ? deactivatePackage(id) : activatePackage(id),
      );

      if (
        (currentlyActive ? deactivatePackage : activatePackage).fulfilled.match(
          result,
        )
      ) {
        // refresh list
        dispatch(fetchPackages(params));
      } else {
        // revert on failure
        setOptimisticPackages((prev: any[]) =>
          prev.map((pkg) =>
            String(pkg.id) === String(id)
              ? { ...pkg, status: currentlyActive ? "active" : "inactive" }
              : pkg,
          ),
        );
      }
    } catch (error) {
      // revert on error
      setOptimisticPackages((prev: any[]) =>
        prev.map((pkg) =>
          String(pkg.id) === String(id)
            ? { ...pkg, status: currentlyActive ? "active" : "inactive" }
            : pkg,
        ),
      );
      console.error("Erro ao alternar status do pacote:", error);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    packages: {
      ...packages,
      data: optimisticPackages,
    },
    loading,
    error,
    setPackageId,
    packageId,
    handleDelete,
    setFormData,
    services,
    getValues,
    control,
    watch,
    handlePage,
    params,
    currentPage,
    pagesArray,
    totalPages,
    handlePageChange,
    currency,
    handleFilterChange,
    searchValue,
    pendingImageFile,
    setPendingImageFile,
    handleToggleActive,
  };
};

export default usePackage;
